import DefaultTable, {DefaultTableProps} from '../../../common/components/table/DefaultTable';
import {getAssigneeId} from '../../../../../common/model/types/assignees/getAssigneeId';
import {Assignee} from '../../../../../common/model/types/assignees/Assignee';
import {AssigneesSearchResultLevel} from '../../../../../common/model/types/assignees/AssigneesSearchResultLevel';
import {useControllers} from '../../../../../common/helpers/react/hooks/useControllers';
import {getTableLevelByData} from '../groups/wizard/agents-step/getTableLevelByData';
import {Factory} from '../../../../../common/types/common/functions/Factory';
import {ColumnDescription} from '../../../common/components/table/ColumnDescription';
import React, {useMemo} from 'react';
import {tAsString} from '../../../../../common/helpers/react/text/tAsString';
import {getAssigneeName} from '../../../../../common/model/types/assignees/getAssigneeName';
import {Icon} from '@sabre/spark-react-core';
import {getCityAndCountry} from '../groups/wizard/agents-step/getCityAndCountry';
import AssigneeGroups from '../groups/wizard/agents-step/AssigneeGroups';
import {useOnce} from '../../../../../common/helpers/react/hooks/useOnce';
import {noop} from '../../../../../common/helpers/functions/noop';
import {asNumber} from '../../../../../common/helpers/converters/asNumber';
import {AddRemoveInfo} from '../groups/wizard/agents-step/AddRemoveInfo';
import {toServerSortingConfig} from '../../../common/components/table/helpers/toServerSortingConfig';

export type AssigneesTableProps = DefaultTableProps<Assignee> & {
    groupId?: number,
    addRemoveInfo?: AddRemoveInfo
}

/**
 * Column the server orders each result level by when no explicit order is requested.
 */
const DEFAULT_SORT_BY: Partial<Record<AssigneesSearchResultLevel, string>> = {
    [AssigneesSearchResultLevel.PCC]: 'sabreId',
    [AssigneesSearchResultLevel.DOMESTIC]: 'pcc',
    [AssigneesSearchResultLevel.INTERNATIONAL]: 'name'
};

export default function AssigneesTable(props: AssigneesTableProps) {
    const groups = useControllers().groups;
    const data = props.groupId !== undefined
        ? groups.getAssigneesForAddAssigneeTable$(props.groupId, props.addRemoveInfo).useState()
        : groups.getAssigneesSearchResults$().useState()?.data;

    const getSearchCallFunction: Factory<() => void> = useOnce(() => {

        return function (row: Assignee) {
            return row.agent
                ? noop()
                : () => groups.searchForPossibleAssignees({
                    id: asNumber(getAssigneeId(row))
                }).andWeAreDone();
        };
    });

    const columns = useColumns(data, getSearchCallFunction);

    const level = useTableLevel(data);

    const sortingState = groups.getAssigneesSortingState$().useState();

    const rowProps = useMemo(() => (row: Assignee) => {
        return {
            onDoubleClick: getSearchCallFunction(row)
        };
    }, []);

    const sorting = sortingState?.supported
        ? toServerSortingConfig(sortingState, (sortBy, sortOrder) =>
            groups.changeAssigneesSorting(sortBy, sortOrder), DEFAULT_SORT_BY[level])
        : undefined;

    return <DefaultTable
        {...props}
        idGetter={getAssigneeId}
        data={data}
        columns={columns}
        rowProps={rowProps}
        sorting={sorting}
        />;
}

function useTableLevel(data?: Assignee[]): AssigneesSearchResultLevel {
    const groups = useControllers().groups;
    const results = groups.getAssigneesSearchResults$().useState();

    if (results?.level === AssigneesSearchResultLevel.GLOBAL) {
        return getTableLevelByData(data);
    } else if (results?.level === AssigneesSearchResultLevel.SEARCH) {
        return getTableLevelByData(data);
    } else {
        return results?.level ?? AssigneesSearchResultLevel.SEARCH;
    }
}

function useColumns(data: Assignee[] | undefined, getSearchCallFunction: Factory<() => void>) {
    const level = useTableLevel(data);

    const CHEVRON_COLUMN: ColumnDescription<Assignee> = useMemo(() => {
        return {
            size: 1,
            align: 'right',
            ariaLabel: tAsString('LINK'),
            render: function (row: Assignee) {
                return row.agent
                    ? <></>
                    : <a
                        aria-label={tAsString('SEARCH_FOR_ASSIGNEES_IN_X', {
                            name: getAssigneeName(row)
                        })}
                        href={document.location.hash}
                        onClick={getSearchCallFunction(row)}
                    >
                        <Icon name={'arrow-chevron-right'}/>
                    </a>;
            }
        };
    }, []);

    const defaultColumns: ColumnDescription<Assignee>[] = useMemo(() => [
        {
            header: tAsString('NAME'),
            render: row => <>{getAssigneeName(row)}</>
        },
        CHEVRON_COLUMN
    ], []);

    const pccColumns = useMemo<ColumnDescription<Assignee>[]>(() => [
        {
            size: 2,
            header: tAsString('PCC_EPR'),
            sortable: true,
            sortKey: 'sabreId',
            render: row => <>{row.agent?.pcc_ref?.code}/{row.agent?.epr}</>
        },
        {
            size: 2,
            header: tAsString('AGENT_NAME_HEADER'),
            sortable: true,
            sortKey: 'name',
            render: row => <>{row.agent?.name}</>
        },
        {
            size: 2,
            header: tAsString('LOCATION_HEADER'),
            sortable: true,
            sortKey: 'location',
            render: row => <>{
                getCityAndCountry(row.agent?.pcc_ref?.location_ref)
            }</>
        },
        {
            size: 3,
            header: tAsString('AGENCY_NAME_HEADER'),
            sortable: true,
            sortKey: 'agencyName',
            render: row => <>
                {row.agent?.pcc_ref?.agency_subscriber_name}
            </>
        },
        {
            size: 3,
            header: tAsString('GROUP_NAMES_HEADER'),
            sortable: true,
            sortKey: 'groups',
            render: row => <AssigneeGroups assignee={row}/>
        },
        {
            size: 2,
            header: tAsString('LOCK_STATUS'),
            sortable: true,
            sortKey: 'lockStatus',
            render: row => <>{row.agent?.locked ? tAsString('LOCKED') : ''}</>
        }
    ], []);

    const domesticColumns = useMemo<ColumnDescription<Assignee>[]>(() => [
        {
            size: 2,
            header: tAsString('PCC'),
            sortable: true,
            sortKey: 'pcc',
            render: row => <>{getAssigneeName(row)}</>
        },
        {
            size: 2,
            header: tAsString('LOCATION_HEADER'),
            sortable: true,
            sortKey: 'location',
            render: row => <>{getCityAndCountry(row.pcc?.location_ref)}</>
        },
        {
            size: 3,
            header: tAsString('AGENCY_NAME_HEADER'),
            sortable: true,
            sortKey: 'agencyName',
            render: row => <>
                {row.pcc?.agency_subscriber_name}
            </>
        },
        {
            size: 4,
            header: tAsString('GROUP_NAMES_HEADER'),
            sortable: true,
            sortKey: 'groups',
            render: row => <AssigneeGroups assignee={row}/>
        },
        CHEVRON_COLUMN
    ], []);

    const internationalColumns = useMemo<ColumnDescription<Assignee>[]>(() => [
        {
            size: 4,
            header: tAsString('NAME'),
            sortable: true,
            sortKey: 'name',
            render: row => <>{getAssigneeName(row)}</>
        },
        {
            size: 3,
            header: tAsString('COUNTRY_HEADER'),
            sortable: true,
            sortKey: 'country',
            render: row => <>{
                row.agency?.is_international
                    ? tAsString('INTERNATIONAL_AGENCY_LOCATION_PLACEHOLDER')
                    : row.agency?.location_ref?.country ?? tAsString('N/A')
            }</>
        },
        {
            size: 4,
            header: tAsString('GROUP_NAMES_HEADER'),
            sortable: true,
            sortKey: 'groups',
            render: row => <AssigneeGroups assignee={row}/>
        },
        CHEVRON_COLUMN
    ], []);

    switch (level) {
        case AssigneesSearchResultLevel.PCC:
            return pccColumns;
        case AssigneesSearchResultLevel.DOMESTIC:
            return domesticColumns;
        case AssigneesSearchResultLevel.INTERNATIONAL:
            return internationalColumns;
        case AssigneesSearchResultLevel.SEARCH:
        default:
            return defaultColumns;
    }
}